import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { holloLiveCardStyles } from "./hollo-live-card-styles";

@customElement("hollo-live-card")
export class HolloLiveCard extends LitElement {
  @property() fileId = "";
  @property() alt = "";
  @property({ type: String }) videoSrc?: string; // blob: or data:
  @property({ type: Boolean }) loop = false; // loop idle video only
  @property({ type: Boolean }) loading = false;
  @property({ type: Boolean }) failed = false;
  @property({ type: Boolean }) reconnecting = false;
  @state() private recording = false;
  @state() private errMsg = "";
  @state() private permissionHint = false;

  render() {
    return html`
      <div class="hollo-live-card">
        ${this.videoSrc && html``}
        ${this.loading
          ? html`<div
              class="hollo-mini-spinner"
              role="status"
              aria-label="Loading video"
            ></div>`
          : this.videoSrc
          ? html`<div class="hollo-portrait-container">
              <video
                class="hollo-portrait"
                preload="auto"
                playsinline
                ?loop=${this.loop}
                @loadedmetadata=${this.onLoadedMeta}
                @ended=${this.onVideoEnded}
              ></video>
            </div>`
          : this.failed
          ? html`<p>No video</p>`
          : html``}
        ${this.errMsg
          ? html`<div class="hollo-error" role="alert">${this.errMsg}</div>`
          : null}
        ${this.permissionHint && !this.recording
          ? html`<div class="hollo-perm-hint" role="status">
              Please allow microphone access in your browser
            </div>`
          : null}
      </div>
    `;
  }

  // When Lit applies a new videoSrc, force the <video> to reload & play.
  protected updated(changed: Map<string, unknown>) {
    if (changed.has("videoSrc")) {
      const video = this.shadowRoot?.querySelector(
        "video.hollo-portrait"
      ) as HTMLVideoElement | null;
      if (!video) return;
      video.src = this.videoSrc || "";
      video.load();
      video.currentTime = 0;
      video.play().catch((e) => {
        // Surface a succinct message if the browser can't decode the stream
        const name = (e && e.name) || "";
        if (name === "NotSupportedError") {
          this.errMsg =
            "This browser build can't decode the idle video. Use WebM (VP8/VP9) or H.264 MP4.";
        } else {
          // this.errMsg = "Couldn't start video playback.";
        }
        // leave controls hidden; user gesture won't help if codec is unsupported
      });
    }
  }

  private pauseVideoPlayback(pauseAllAndNotIdleVideo = false) {
    const video = this.shadowRoot?.querySelector(
      "video.hollo-portrait"
    ) as HTMLVideoElement | null;
    if (video) {
      try {
        video.pause();
        video.currentTime = 0;
      } catch {}
    }
    if (pauseAllAndNotIdleVideo) {
      this.dispatchEvent(
        new CustomEvent("load-idle-video", { bubbles: true, composed: true })
      );
    }
  }

  private onLoadedMeta = () => {
    const video = this.shadowRoot?.querySelector(
      "video.hollo-portrait"
    ) as HTMLVideoElement | null;
    if (!video) return;
    try {
      video.play().catch(() => {});
    } catch {}
  };

  // Public method to play the currently attached video immediately
  public playCurrentVideo() {
    const video = this.shadowRoot?.querySelector(
      "video.hollo-portrait"
    ) as HTMLVideoElement | null;
    if (!video) return;
    try {
      // ensure we are at start and try to play
      if (video.currentTime > 0 && !this.loop) {
        try {
          video.currentTime = 0;
        } catch {}
      }
      video.play().catch(() => {});
    } catch {}
  }

  // Public method to stop the currently attached video immediately
  public stopCurrentVideo() {
    try {
      this.pauseVideoPlayback(true);
    } catch {}
  }

  private onVideoEnded = () => {
    this.dispatchEvent(
      new CustomEvent("live-video-ended", { bubbles: true, composed: true })
    );
  };

  static styles = holloLiveCardStyles;
}

declare global {
  interface HTMLElementTagNameMap {
    "hollo-live-card": HolloLiveCard;
  }
}
